/**
 * Funding Source Color Mapping
 *
 * Maps funding source names to their respective colors.
 * Used across Daily, Weekly, Monthly views and modals.
 *
 * Funding Sources in DB:
 * 1. ביטוח לאומי (National Insurance)
 * 2. משרד הביטחון (Ministry of Defense)
 * 3. לאומית (Leumit)
 * 4. כללית (Clalit)
 * 5. מאוחדת (Meuhedet)
 * 6. מכבי (Maccabi)
 * 7. פרטי (Private)
 */

/** Attendance mark meaning the patient did not come (AttendanceStatuses.Missed on the server). */
const ATTENDANCE_MISSED = 2;

export interface FundingSourceColorInfo {
  borderColor: string;
  backgroundColor: string;
  textColor: string;
  badgeBg: string;
  name: string;
}

// Color map by funding source name (case-insensitive match)
const FUNDING_SOURCE_COLORS: Record<string, FundingSourceColorInfo> = {
  // כללית - Clalit (Blue)
  כללית: {
    borderColor: "#0099CC",
    backgroundColor: "rgba(0, 153, 204, 0.08)",
    textColor: "#0099CC",
    badgeBg: "rgba(0, 153, 204, 0.15)",
    name: "כללית",
  },

  // מכבי - Maccabi (Orange/Yellow)
  מכבי: {
    borderColor: "#FFB81C",
    backgroundColor: "rgba(255, 184, 28, 0.08)",
    textColor: "#CF9500",
    badgeBg: "rgba(255, 184, 28, 0.15)",
    name: "מכבי",
  },

  // מאוחדת - Meuhedet (Green)
  מאוחדת: {
    borderColor: "#00A651",
    backgroundColor: "rgba(0, 166, 81, 0.08)",
    textColor: "#00A651",
    badgeBg: "rgba(0, 166, 81, 0.15)",
    name: "מאוחדת",
  },

  // לאומית - Leumit (Red)
  לאומית: {
    borderColor: "#E31E24",
    backgroundColor: "rgba(227, 30, 36, 0.08)",
    textColor: "#E31E24",
    badgeBg: "rgba(227, 30, 36, 0.15)",
    name: "לאומית",
  },

  // פרטי - Private (Purple)
  פרטי: {
    borderColor: "#6B46C1",
    backgroundColor: "rgba(107, 70, 193, 0.08)",
    textColor: "#6B46C1",
    badgeBg: "rgba(107, 70, 193, 0.15)",
    name: "פרטי",
  },

  // ביטוח לאומי - National Insurance (Teal)
  "ביטוח לאומי": {
    borderColor: "#0D9488",
    backgroundColor: "rgba(13, 148, 136, 0.08)",
    textColor: "#0D9488",
    badgeBg: "rgba(13, 148, 136, 0.15)",
    name: "ביטוח לאומי",
  },

  // משרד הביטחון - Ministry of Defense (Indigo/Vibrant Blue)
  "משרד הביטחון": {
    borderColor: "#4F46E5",
    backgroundColor: "rgba(79, 70, 229, 0.08)",
    textColor: "#4F46E5",
    badgeBg: "rgba(79, 70, 229, 0.15)",
    name: "משרד הביטחון",
  },
};

// Default color for unknown/other (Gray)
export const DEFAULT_COLOR: FundingSourceColorInfo = {
  borderColor: "#9CA3AF",
  backgroundColor: "rgba(156, 163, 175, 0.08)",
  textColor: "#6B7280",
  badgeBg: "rgba(156, 163, 175, 0.15)",
  name: "",
};

// Other Event Color (Elegant Amber/Gold)
export const OTHER_EVENT_COLOR: FundingSourceColorInfo = {
  borderColor: "#D97706", // Amber 600
  backgroundColor: "#FEF3C7", // Amber 100
  textColor: "#92400E",
  badgeBg: "rgba(245, 158, 11, 0.15)",
  name: "אחר",
};

/**
 * Appointment Status Colors (Time-based - stronger saturation, opaque to cover grid borders)
 */
export const PAST_COLOR: FundingSourceColorInfo = {
  borderColor: "#64748B", // Slate 600 - stronger gray
  backgroundColor: "#E2E8F0", // Slate 200 - opaque, covers grid lines
  textColor: "#334155",
  badgeBg: "rgba(100, 116, 139, 0.25)",
  name: "עבר",
};

/**
 * Someone said the patient did not come. Same slate body as any other past appointment so history
 * does not turn into a wall of red — only the accent, which every view renders, carries the fact.
 * Unlike a guess drawn from a missing report, this one was actually stated by a person.
 */
export const MISSED_COLOR: FundingSourceColorInfo = {
  borderColor: "#DC2626", // Red 600
  backgroundColor: "#E2E8F0", // Slate 200, same as PAST_COLOR
  textColor: "#334155",
  badgeBg: "rgba(220, 38, 38, 0.2)",
  name: "לא הגיע",
};

export const PRESENT_COLOR: FundingSourceColorInfo = {
  borderColor: "#7C3AED", // Violet 600 - stronger purple
  backgroundColor: "#EDE9FE", // Violet 100 - opaque, covers grid lines
  textColor: "#5B21B6",
  badgeBg: "rgba(124, 58, 237, 0.25)",
  name: "בהווה",
};

export const FUTURE_COLOR: FundingSourceColorInfo = {
  borderColor: "#059669", // Emerald 600 - stronger green
  backgroundColor: "#D1FAE5", // Emerald 100 - opaque, covers grid lines
  textColor: "#047857",
  badgeBg: "rgba(5, 150, 105, 0.25)",
  name: "עתיד",
};

/**
 * Get color info for a funding source by name
 * @deprecated Use getAppointmentColors for time-based states
 */
export function getFundingSourceColor(
  fundingSourceName?: string
): FundingSourceColorInfo {
  if (!fundingSourceName) return DEFAULT_COLOR;

  const trimmedName = fundingSourceName.trim();

  // Try exact match first
  if (FUNDING_SOURCE_COLORS[trimmedName]) {
    return FUNDING_SOURCE_COLORS[trimmedName];
  }

  // Try partial match (contains) for flexibility
  for (const [key, value] of Object.entries(FUNDING_SOURCE_COLORS)) {
    if (trimmedName.includes(key) || key.includes(trimmedName)) {
      return value;
    }
  }

  return DEFAULT_COLOR;
}

/**
 * Helper to get the correct color for an appointment based on its time relative to now
 * @param appointment The appointment object
 * @returns The appropriate color info (Past, Present, or Future)
 */
export function getAppointmentColors(appointment: any): FundingSourceColorInfo {
  // 1. Check for "Other" events (no patient/group AND no mediaId)
  // These ALWAYS get the Yellow color
  const isOther = !appointment.patientName && !appointment.treatmentGroupName && !appointment.mediaId;
  const isGoogle = appointment.source === 'google_calendar';

  if (isOther || isGoogle) {
    return OTHER_EVENT_COLOR;
  }

  // Marked as a no-show: stated by a person, and only possible once the appointment has started,
  // so it is always a past session and outranks the time-based colours below.
  if (appointment.attendanceStatusId === ATTENDANCE_MISSED) {
    return MISSED_COLOR;
  }

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  
  if (!appointment.date) {
    return FUTURE_COLOR;
  }

  // Parse appointment date
  const [year, month, day] = appointment.date.split("-").map(Number);
  const aptDate = new Date(year, month - 1, day);

  // If no time is specified, color based on the date only
  if (!appointment.time) {
    if (aptDate < today) {
      return PAST_COLOR;
    } else if (aptDate.getTime() === today.getTime()) {
      return PRESENT_COLOR;
    } else {
      return FUTURE_COLOR;
    }
  }

  // Parse start time
  const [startH, startM] = appointment.time.split(":").map(Number);
  const startDateTime = new Date(year, month - 1, day, startH, startM);
  
  let endDateTime: Date;
  if (appointment.endTime) {
    const [endH, endM] = appointment.endTime.split(":").map(Number);
    endDateTime = new Date(year, month - 1, day, endH, endM);
  } else {
    // Default duration 45 mins if not specified
    const duration = appointment.duration || 45;
    endDateTime = new Date(startDateTime.getTime() + duration * 60000);
  }

  // Determine state
  if (now > endDateTime) {
    return PAST_COLOR;
  } else if (now >= startDateTime && now <= endDateTime) {
    return PRESENT_COLOR;
  } else {
    return FUTURE_COLOR;
  }
}

/**
 * Get all unique funding source colors for legend display
 */
export function getLegendColors(): Array<{ name: string; color: string }> {
  return [
    { name: "כללית", color: "#0099CC" },
    { name: "מכבי", color: "#FFB81C" },
    { name: "מאוחדת", color: "#00A651" },
    { name: "לאומית", color: "#E31E24" },
    { name: "פרטי", color: "#6B46C1" },
    { name: "ביטוח לאומי", color: "#0D9488" },
    { name: "משרד הביטחון", color: "#4F46E5" },
    { name: "אחר", color: "#F59E0B" },
  ];
}

/**
 * Truncate text to a maximum length and add ellipsis
 */
export function truncateText(text: string | null | undefined, maxLength: number = 25): string {
  if (!text) return "";
  const singleLineText = text.replace(/\r?\n|\r/g, " ");
  if (singleLineText.length <= maxLength) return singleLineText;
  return singleLineText.substring(0, maxLength) + "...";
}
